import { useForm } from 'react-hook-form';

import { SimpleModalVerticalActionsStyled } from 'lib/modal/simple-modal.styles';
import { ActivityBuilderModal } from 'approot/activity-builder/activity-builder-modal';
import {
  InputDescriptionStyled,
  LabelStyled,
  TextInputStyled,
} from 'approot/shared/styles/inputs.style';
import { Box } from 'approot/shared/design-system/box/box.style';
import { NegativeButton } from 'approot/shared/styles/buttons/negative-button';
import { PStyled } from 'approot/shared/styles/typography.style';
import { red9 } from 'approot/shared/styles/colours.style';
import ValidationMessage from 'approot/shared/validation/validation-message';
import AutoResizingTextArea from 'approot/shared/auto-resizing-textarea/auto-resizing-textarea';

const MAJOR_FIELD = 'major-field';
const MAJOR_DESCRIPTION_FIELD = 'major-description-field';

type FormData = {
  [MAJOR_FIELD]: string;
  [MAJOR_DESCRIPTION_FIELD]: string;
};

type Props = {
  triggerMajorUpdate: (description: string) => void;
  closeModal: () => void;
};

const SettingsPublishMajorModal = ({
  triggerMajorUpdate,
  closeModal,
}: Props) => {
  const loading = false;

  const {
    register,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>();

  const description = watch(MAJOR_DESCRIPTION_FIELD);

  const onSubmit = (data: FormData) => {
    triggerMajorUpdate(data[MAJOR_DESCRIPTION_FIELD]);
    closeModal();
  };

  return (
    <ActivityBuilderModal
      title="Publish Major Update"
      headerTitle="Publish Major Update"
      isVisible
      onClose={closeModal}
    >
      <PStyled colour={red9}>
        Use a major update when your changes could affect how the lesson or
        assessment is delivered or how a student might answer. For example, if
        you’ve added new components, images, or activities.
      </PStyled>
      <PStyled colour={red9}>
        <strong>
          If your edits are only small text changes, a minor update may be more
          appropriate.
        </strong>
      </PStyled>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Box $flexDirection="column" $marginBottom={24}>
          <LabelStyled htmlFor={MAJOR_DESCRIPTION_FIELD}>
            Describe the changes made to this lesson
          </LabelStyled>
          <AutoResizingTextArea
            data-selector="txt-change-description"
            id={MAJOR_DESCRIPTION_FIELD}
            {...register(MAJOR_DESCRIPTION_FIELD, {
              required: 'Please provide a description',
            })}
            initialValue={description}
            showCounter
            rows={3}
            hasError={!!errors?.[MAJOR_DESCRIPTION_FIELD]}
            maxLength={100}
          />
          <ValidationMessage
            errors={errors}
            field={MAJOR_DESCRIPTION_FIELD}
            id={`${MAJOR_DESCRIPTION_FIELD}-error-message`}
          />
          <InputDescriptionStyled>
            Provide a brief summary of what’s been updated or improved in this
            lesson. Keep it clear and concise—this summary will be visible to
            teachers to help them understand what has changed.
          </InputDescriptionStyled>
        </Box>
        <Box $flexDirection="column" $marginBottom={4}>
          <LabelStyled htmlFor={MAJOR_FIELD}>
            To confirm, type MAJOR below
          </LabelStyled>
          <TextInputStyled
            data-selector="txt-confirm-major"
            type="text"
            autoComplete="off"
            id={MAJOR_FIELD}
            {...register(MAJOR_FIELD, {
              validate: value =>
                value.toUpperCase() !== 'MAJOR'
                  ? 'The value does not match MAJOR'
                  : true,
            })}
            aria-required
            aria-invalid={!!errors?.[MAJOR_FIELD]}
            aria-describedby={`${MAJOR_FIELD}-error-message`}
            $hasError={!!errors?.[MAJOR_FIELD]}
          />
          <ValidationMessage
            errors={errors}
            field={MAJOR_FIELD}
            id={`${MAJOR_FIELD}-error-message`}
          />
        </Box>
        <SimpleModalVerticalActionsStyled>
          <NegativeButton
            type="submit"
            loading={loading}
            disabled={loading}
            data-selector="btn-confirm-major-update"
          >
            Confirm Major Update
          </NegativeButton>
        </SimpleModalVerticalActionsStyled>
      </form>
    </ActivityBuilderModal>
  );
};

export default SettingsPublishMajorModal;
